import type { MatchSeat } from './names';

/**
 * Refresh-proof ARENA MATCH state (defect 19, rule R3).
 *
 * The server holds only the RECEIPT for a live match, never its state: `POST
 * /arena/br/enter` charges the entry, opens an ArenaBrMatch row and returns its id;
 * the whole 25-fighter fight runs client-side in views/arena-match.ts; and `POST
 * /arena/br/result` resolves that one row, exactly once. So a "rejoin" is impossible
 * — surviving a reload has to be a client-side snapshot, and this module is the whole
 * of it: the shape, the storage, and the one pure decision about whether a snapshot
 * found on boot is still worth restoring. The `matchId` rides along because a
 * restored fight still has to be reported against the entry that paid for it.
 *
 * WHAT IS AND IS NOT IN HERE
 * Everything derivable from the seed is deliberately absent: the field and its nav,
 * the fighter list and slot→name mapping, every hero's appearance, the bots' rolled
 * gear and their AI profiles. All of that is rebuilt by placeHeroes() from the same
 * seed and cannot disagree with what the player left. Only the genuinely mutable
 * half is carried: where everyone is, how hurt they are, what they have banked and
 * the match clocks.
 *
 * ONE THING CANNOT BE CARRIED: each CombatState owns a live `rng: () => number`
 * closure (mulberry32) whose counter is captured, not readable. A restored match
 * therefore continues on a fresh draw — positions, HP, gold and kills are exact, but
 * what the bots decide NEXT is a different (still fair, still deterministic-from-here)
 * continuation of the same fight. See RESTORE FIDELITY in the summary.
 *
 * sessionStorage, not localStorage: a match belongs to the tab that is fighting it.
 * localStorage would let a second tab open straight into someone else's fight and
 * would keep a dead match alive for days after the browser closed.
 */

export const MATCH_KEY = 'tb.arena.match';

/**
 * How far the wall clock may have moved between the snapshot and the restore.
 *
 * Under this the clocks jump forward and the world does NOT: nobody is simulated
 * across the gap (battleStep depends on async nav requests and on the per-hero RNG
 * that cannot be restored, so a catch-up sim would diverge from the snapshot it is
 * supposed to continue). Over it, the player has been gone long enough that handing
 * back a fight they did not watch is worse than ending it, so the snapshot is
 * discarded. A judgement call, not a derived number — the owner should confirm it.
 */
export const MATCH_CATCHUP_MS = 15_000;

/** Position/health of one fighter — the part placeHeroes cannot re-derive. */
export interface HeroSnap {
  id: string;
  x: number; y: number;
  hp: number;
  alive: boolean;
}

/** The mutable half of a CombatState (everything else comes back from the seed). */
export interface CombatSnap {
  id: string;
  kills: number; pendingGold: number; goldBanked: number; streak: number;
  safeCd: number; attackCd: number; aiCd: number; campMs: number;
  /**
   * Snapshots written before 2026-09-02 also carry `healedOnce`; the sanctuary no
   * longer heals, so the field is gone. Reading an old snapshot is safe — the parser
   * copies named fields only, and an extra key is ignored.
   */
  wasInSafe: boolean;
  deathOrder: number; timeAlive: number; entryOrder: number;
  attackTargetId: string | null;
  goalTx: number; goalTy: number; hasGoal: boolean;
  stuckMs: number; wanderCd: number; stallMs: number;
  /**
   * Perception memory is deliberately NOT snapshotted. It used to be, as a
   * `firstSeen` map, back when a sighting was a permanent boolean; it is now a 4 s
   * rolling memory of last-known positions, and a bot that comes back from a reload
   * having to re-acquire what it can currently see is both cheaper and more honest
   * than one restored mid-glimpse. Old snapshots carrying the key still parse.
   */
}

export interface MatchSnapshot {
  /**
   * v2 added `matchId`. A v1 snapshot is REJECTED rather than migrated: it names no
   * server-side match, so the result it would eventually submit is one the server
   * now refuses — restoring it would hand the player a fight that cannot be scored.
   * The only ones in existence are from a tab that was mid-match across the deploy.
   */
  v: 2;
  seed: number;
  seats: MatchSeat[];
  /**
   * The ArenaBrMatch row this entry opened (arena-br.service.ts). Every result —
   * the finish AND the teardown forfeit — is submitted against it, so it has to
   * survive the reload with the rest of the fight or the restored match ends in a
   * refused submit.
   *
   * Empty string only in the receipt the client writes BEFORE the enter request
   * lands: that record exists to prove the entry may already be spent, and it is
   * patched with the real id the moment the response arrives.
   */
  matchId: string;
  /** Coins actually charged for this entry, so the restored forfeit copy stays true. */
  paid: number;
  phase: string;
  /** ms elapsed inside `phase`. */
  phaseT: number;
  /** Duration of `phase`, carried so the staleness decision needs no import. */
  phaseDurMs: number;
  battleClock: number;
  deaths: number;
  /** True once this match's one allowed result has been submitted. */
  resultSent: boolean;
  savedAt: number;
  heroes: HeroSnap[];
  combat: CombatSnap[];
}

export type MatchResumeAction = 'none' | 'resume' | 'restart' | 'stale';

/** Storage can throw outright (private mode, blocked site data) — never on our path. */
export function readMatchSnapshot(): MatchSnapshot | null {
  try {
    const raw = sessionStorage.getItem(MATCH_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<MatchSnapshot>;
    if (v?.v !== 2) return null;
    if (!Number.isFinite(v.seed) || !Number.isFinite(v.savedAt)) return null;
    if (typeof v.matchId !== 'string') return null;
    if (typeof v.phase !== 'string' || !Array.isArray(v.heroes) || !Array.isArray(v.combat)) return null;
    return v as MatchSnapshot;
  } catch { return null; }
}

export function writeMatchSnapshot(s: MatchSnapshot): void {
  try { sessionStorage.setItem(MATCH_KEY, JSON.stringify(s)); } catch { /* nothing to lose */ }
}

export function clearMatchSnapshot(): void {
  try { sessionStorage.removeItem(MATCH_KEY); } catch { /* nothing to lose */ }
}

/**
 * Is this snapshot still a fight worth handing back?
 *
 * Pure, so the decision — the part that must never quietly resurrect a finished
 * match — is testable without a DOM, a canvas or a server.
 *
 * `stale` and `none` are the same outcome for the player (the lobby); they are
 * separate so a caller can say WHY, and so a test can tell "there was nothing" from
 * "there was something and we deliberately threw it away".
 */
export function matchResumeAction(s: MatchSnapshot | null, now: number): MatchResumeAction {
  if (!s) return 'none';
  // Already reported, or already over: a finished match must never come back.
  if (s.resultSent || s.phase === 'end') return 'stale';
  // PAID, BUT THE FIGHT NEVER GOT OFF THE GROUND. startMatch writes a fighter-less
  // record the instant the entry is charged, because composing 25 characters takes
  // seconds and a reload inside that window would otherwise burn a real entry with
  // nothing to hand back. There is no state to restore, so the honest outcome is a
  // fresh match on the entry already paid for. Checked AFTER the guards above: a
  // match whose result is in must never come back, fighters or not.
  if (!s.heroes.length) return 'restart';
  // A clock that moved backwards (NTP, a laptop waking) is treated as no gap rather
  // than as a reason to throw the match away.
  const elapsed = Math.max(0, now - s.savedAt);
  if (elapsed > MATCH_CATCHUP_MS) return 'stale';
  // The battle window itself ran out while the page was gone — there is nothing left
  // to fight, and resuming would drop the player straight onto a results screen built
  // from a state nobody watched.
  if (s.phaseDurMs > 0 && s.phaseT + elapsed >= s.phaseDurMs) return 'stale';
  return 'resume';
}

/** How far the clocks must jump on restore (0 when there is nothing to resume). */
export function catchUpMs(s: MatchSnapshot, now: number): number {
  return Math.min(MATCH_CATCHUP_MS, Math.max(0, now - s.savedAt));
}
